import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import Layout from '../components/layout/Layout';
import { useAtm } from '../context/AtmContext';
import { useAuth } from '../hooks/useAuth';
import { AtmListItem, AtmTheme } from '../types/atm';

/**
 * Page for displaying user's ATMs
 */
const MyAtms: React.FC = () => {
  const { userAtms, loadingUserAtms, fetchUserAtms, deleteExistingAtm, error } = useAtm();
  const { isAuthenticated } = useAuth();

  // State
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'transactions'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Filter and sort ATMs
  const filteredAndSortedAtms = React.useMemo(() => {
    let filtered = userAtms;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        atm =>
          atm.name.toLowerCase().includes(term) ||
          (atm.description && atm.description.toLowerCase().includes(term))
      );
    }

    // Sort ATMs
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'transactions':
          comparison = a.transactionCount - b.transactionCount;
          break;
        case 'date':
        default: {
          // Handle different date formats (Date, Timestamp, string, or null)
          const getDate = (dateValue: any): Date => {
            if (!dateValue) return new Date(0); // Handle null/undefined - use epoch
            if (dateValue instanceof Date) return dateValue;
            if (typeof dateValue === 'string') return new Date(dateValue);
            if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
              return dateValue.toDate();
            }
            return new Date(); // fallback
          };

          const aDate = getDate(a.createdAt);
          const bDate = getDate(b.createdAt);
          comparison = aDate.getTime() - bDate.getTime();
          break;
        }
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [userAtms, searchTerm, sortBy, sortDirection]);

  // Handle delete ATM
  const handleDeleteAtm = async (atmId: string) => {
    try {
      setIsDeleting(true);
      await deleteExistingAtm(atmId);
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Error deleting ATM:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format date
  const formatDate = (date: Date | any): string => {
    // Handle different date formats (Date, Timestamp, string, or null)
    let actualDate: Date;
    if (!date) {
      return 'Unknown'; // Handle null/undefined dates
    } else if (date instanceof Date) {
      actualDate = date;
    } else if (typeof date === 'string') {
      actualDate = new Date(date);
    } else if (typeof date === 'object' && typeof date.toDate === 'function') {
      actualDate = date.toDate();
    } else {
      actualDate = new Date(); // fallback
    }

    return actualDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get theme display name
  const getThemeDisplayName = (theme: AtmTheme): string => {
    return theme.charAt(0).toUpperCase() + theme.slice(1);
  };

  // Get theme gradient for header
  const getThemeGradient = (theme: AtmTheme): string => {
    switch (theme) {
      case AtmTheme.CLASSIC:
        return 'bg-gradient-to-r from-gray-600 to-gray-800';
      case AtmTheme.MODERN:
        return 'bg-gradient-to-r from-purple-600 to-pink-600';
      case AtmTheme.RETRO:
        return 'bg-gradient-to-r from-orange-500 to-yellow-500';
      case AtmTheme.DARK:
        return 'bg-gradient-to-r from-gray-900 to-black';
      default:
        return 'bg-gradient-to-r from-blue-600 to-cyan-600';
    }
  };

  // Get theme border color
  const getThemeBorderColor = (theme: AtmTheme): string => {
    switch (theme) {
      case AtmTheme.CLASSIC:
        return 'border-gray-400';
      case AtmTheme.MODERN:
        return 'border-purple-400';
      case AtmTheme.RETRO:
        return 'border-orange-400';
      case AtmTheme.DARK:
        return 'border-gray-700';
      default:
        return 'border-blue-400';
    }
  };

  // Get theme accent color for stats
  const getThemeAccentColor = (theme: AtmTheme): string => {
    switch (theme) {
      case AtmTheme.CLASSIC:
        return 'text-gray-600';
      case AtmTheme.MODERN:
        return 'text-purple-600';
      case AtmTheme.RETRO:
        return 'text-orange-600';
      case AtmTheme.DARK:
        return 'text-gray-800';
      default:
        return 'text-blue-600';
    }
  };

  // Initial data load
  useEffect(() => {
    fetchUserAtms();
  }, []);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My ATMs</h1>
            <p className="text-gray-600">
              Manage your ATMs and track their usage
              {!isAuthenticated && (
                <span className="text-amber-600 ml-2">
                  (Stored locally - sign in to sync across devices)
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3 mt-4 sm:mt-0">
            <Link
              to="/atms"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border-2 border-blue-600 rounded-md hover:bg-blue-600 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Explore
            </Link>
            <Link
              to="/create-atm"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create ATM
            </Link>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search ATMs..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <select
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as 'date' | 'name' | 'transactions')}
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="transactions">Sort by Usage</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title={`Sort ${sortDirection === 'desc' ? 'ascending' : 'descending'}`}
            >
              {sortDirection === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loadingUserAtms ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading your ATMs...</p>
          </div>
        ) : filteredAndSortedAtms.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            {searchTerm ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <h3 className="text-lg font-medium text-gray-900 mb-2">No ATMs found</h3>
                <p className="text-gray-600 mb-4">
                  No ATMs match your search criteria. Try a different search term.
                </p>
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear search
                </button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-12"
              >
                <div className="text-6xl mb-4">🏧</div>
                <h3 className="text-2xl font-bold mb-2 text-gray-800">No ATMs Yet</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Create your first ATM to get started. Build interactive experiences with custom
                  transactions!
                </p>
                <Link
                  to="/create-atm"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Create Your First ATM
                </Link>
              </motion.div>
            )}
          </div>
        ) : (
          /* ATM Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedAtms.map((atm: AtmListItem, index) => (
              <motion.div
                key={atm.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer border-2 ${getThemeBorderColor(
                  atm.theme
                )} overflow-hidden`}
              >
                {/* Header with gradient - Fixed height for consistency */}
                <div className={`${getThemeGradient(atm.theme)} p-4 min-h-[90px] flex flex-col`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-white/20 text-white">
                      {getThemeDisplayName(atm.theme)}
                    </span>
                    <div
                      className={`flex items-center text-xs text-white/90 ${
                        atm.isPublic ? 'bg-green-500/30' : 'bg-gray-500/30'
                      } px-2 py-1 rounded-full`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full mr-1 ${
                          atm.isPublic ? 'bg-green-300' : 'bg-gray-300'
                        }`}
                      ></div>
                      {atm.isPublic ? 'Public' : 'Private'}
                    </div>
                  </div>
                  <Link
                    to={`/atm/${atm.id}`}
                    className="block"
                    onClick={e => e.stopPropagation()}
                  >
                    <h3 className="text-xl font-bold text-white truncate hover:text-white/90 transition-colors">
                      {atm.name}
                    </h3>
                  </Link>
                </div>

                {/* Stats */}
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center bg-gray-50 rounded p-2">
                      <p className={`text-2xl font-bold ${getThemeAccentColor(atm.theme)}`}>
                        {atm.transactionCount}
                      </p>
                      <p className="text-xs text-gray-600">Transactions</p>
                    </div>
                    <div className="text-center bg-gray-50 rounded p-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(atm.createdAt)}
                      </p>
                      <p className="text-xs text-gray-600">Created</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Link
                      to={`/atm/${atm.id}`}
                      className="flex-1 text-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={e => e.stopPropagation()}
                    >
                      View
                    </Link>
                    <Link
                      to={`/edit-atm/${atm.id}`}
                      className="flex-1 text-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      onClick={e => e.stopPropagation()}
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setDeleteConfirmId(atm.id);
                      }}
                      className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete ATM</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this ATM? This action cannot be undone and all
                transaction history will be lost.
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteAtm(deleteConfirmId)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyAtms;
